import { Component } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import moment from "moment"
import pluralize from "pluralize"
import { get, includes, keys, isEmpty } from "lodash"
import { IconButton } from "@mui/material"
import { GetApp, NotificationsActive, NotificationsOff, Star, StarBorder } from "@mui/icons-material"
import { DATABASE_DATE_FORMAT } from "helpers/formatters/dates"

import { Browse, Bundle, Missing } from "./cards"

import "./style.scss"

class Dataset extends Component {
  static propTypes = {
    dataset: PropTypes.object.isRequired,
    displayStatus: PropTypes.bool,
    actions: PropTypes.object,
    view: PropTypes.oneOf(["account", "browse"]).isRequired,
    style: PropTypes.object,
    tracking: PropTypes.shape({
      contactUs: PropTypes.func,
      reactivate: PropTypes.func,
    }),
  }

  constructor(props) {
    super(props)
    this.state = {
      expanded: false,
      favourite: false,
      notifications: false,
    }
  }

  toggleExpanded = () => {
    const { expanded } = this.state
    this.setState({ expanded: !expanded })
  }

  toggleFavourite = () => {
    const { favourite } = this.state
    this.setState({ favourite: !favourite })
    return false
  }

  toggleNotifications = () => {
    const { notifications } = this.state
    this.setState({ notifications: !notifications })
    return false
  }

  expiration = () => {
    const { expiration } = this.props.dataset
    if (!expiration) { return null }
    const nbDays = moment(expiration, DATABASE_DATE_FORMAT).diff(moment(), "days")
    let message
    // expired
    if (nbDays < 0) {
      message = "subscription is expired"
      if (nbDays >= -30) {
        message = `${message} (${Math.abs(nbDays)} ${pluralize("day", nbDays)})`
      }
    // expires today
    } else if (nbDays === 0) {
      message = "subscription expires today"
      // expires soon
    } else if (nbDays <= 30) {
      message = `subscription expires in ${nbDays} ${pluralize("day", nbDays)}`
    // expires in a long time so dont show the text
    } else {
      return null
    }

    return <div className={classNames("preheader", { expired: nbDays < 0, urgent: nbDays < 15 })}>
      {message}
    </div>
  }

  featured = () => {
    const { dataset } = this.props
    const { featured } = dataset
    if (!featured) { return null }

    return <div className="preheader">Featured</div>
  }

  lastAvailableDate = () => {
    const currentDate = get(this.props, "dataset.availability.currentDate")
    if (!currentDate) { return null }

    return <div className="subheader">
      Last available date is: <span className="date">{moment(currentDate).format("MMM D, YYYY")}</span>
    </div>
  }

  preheader = () => {
    const { view } = this.props
    return view === "browse" ? this.featured() : this.expiration()
  }

  subheader = () => {
    const { view } = this.props
    return view === "browse" ? null : this.lastAvailableDate()
  }

  actions = () => {
    const { actions: pActions, view } = this.props
    const { favourite, notifications } = this.state
    const saleSheet = get(this.props, "dataset.saleSheet")

    const actions = {
      favourite: view === "browse",
      notifications: view === "account",
      download: true,
      info: true,
      ...pActions,
    }

    return <>
      { actions.favourite
        && <IconButton
          aria-label="favorite"
          className="favourite"
          onClick={this.toggleFavourite}
          size="large"
        >
          { favourite ? <Star className="favourite" /> : <StarBorder />}
        </IconButton> }

      { actions.notifications
        && <IconButton
          aria-label="notification"
          className="notification"
          onClick={this.toggleNotifications}
          size="large"
        >
          {notifications ? <NotificationsActive className="notifications-on" /> : <NotificationsOff className="notifications-off" />}
        </IconButton> }

      { actions.download && saleSheet
        && <IconButton aria-label="download" className="download" size="large">
          <a target="_blank" rel="noreferrer" href={saleSheet} title="Download sale sheet"><GetApp /></a>
        </IconButton> }
    </>
  }

  missingMetadata = () => {
    const { dataset } = this.props
    return !includes(keys(dataset), "name")
  }

  contactUs = (e) => {
    e.stopPropagation()
    const { dataset: { id, name, isBundle }, tracking } = this.props
    window.Intercom("showNewMessage", `I would like to request access to the ${name} (${id}) ${isBundle ? "bundle" : "dataset"}`)
    tracking.contactUs()
  }

  reactivate = (e) => {
    e.stopPropagation()
    const { dataset: { id, name, isBundle }, tracking } = this.props
    window.Intercom("showNewMessage", `I would like to re-activate the ${name} (${id}) ${isBundle ? "bundle" : "dataset"}`)
    tracking.reactivate()
  }

  render() {
    const { view, displayStatus, dataset, style } = this.props
    if (isEmpty(dataset) || this.missingMetadata()) {
      return <Missing id={get(dataset, "id")} />
    }

    switch (true) {
      case view === "browse" && dataset.isBundle:
        return <Bundle
          style={style}
          displayStatus={displayStatus}
          dataset={dataset}
          state={this.state}
          missingMetadata={this.missingMetadata()}
          actions={this.actions()}
          preheader={this.preheader()}
          subheader={this.subheader()}
          toggleExpanded={this.toggleExpanded}
          contactUs={this.contactUs}
        />
      case view === "browse" && !dataset.isBundle:
      default:
        return <Browse
          style={style}
          displayStatus={displayStatus}
          dataset={dataset}
          state={this.state}
          missingMetadata={this.missingMetadata()}
          actions={this.actions()}
          preheader={this.preheader()}
          subheader={this.subheader()}
          toggleExpanded={this.toggleExpanded}
          contactUs={this.contactUs}
          reactivate={this.reactivate}
        />
    }
  }
}

export default Dataset
